/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import set from 'lodash.set';

import { ActionManagerInstance } from '../../actions/manager';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { OPEN_EMBEDDED_DATA_MODAL, ERROR } from '../../store/state/actions';
import { dispatch, getDataObject, getValue } from '../../store/state/state-manager';
import type { Action } from '../../store/state/state-manager';
import ContextProcessor from '../../context/context-processor';
import {
  processDataToDispatchErrorAction,
  getContext,
  processViewResponse,
  getActionPayload,
  getActionBasedOnContainerMode,
  addPreviewResetAction
} from '../../store/state/epics/util';
import { hasErrorActionType } from '../common-helper';
import { buildModalContext } from '../../utils/context-utils';
import { getTableRowErrorMessages } from '../../messages';
import ObjectTransformer from '../../utils/object-transformer';
import constants, { publicConstants } from '../../constants';
import Utils from '../../utils/utils';
import stateMachineManager from '../../page-instructions/state-machine-manager';
import * as actions from '../../store/state/actions/action-creators';
import type { DxAPIResponseObject } from '../types';

import {
  generateInstructions,
  getActionAndAssignmentID,
  updateCaseInfoContentForPageGroup,
  updateClassIDInInitialContent
} from './helper';
import { executeRequest } from './async-request';
import type { ActionPayload } from './types';

export const openEmbeddedDataModal = (action$: Observable<{ type: Action['type']; payload: ActionPayload }>) => {
  let actionMgrID: string | number = 0;
  let actionsOut: any[] = [];
  let toContext = '';
  return action$.pipe(
    ofType(OPEN_EMBEDDED_DATA_MODAL),
    map((action: { type: Action['type']; payload: ActionPayload }) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((payload: ActionPayload) => {
      return executeRequest(payload);
    }),
    mergeMap(async (data) => {
      if (data && data.type === ERROR) {
        actionsOut.push(processDataToDispatchErrorAction(data));
        return {
          actionsOut,
          actionInPayload: {
            skipPageInstructions: true
          }
        };
      }
      const { actionInPayload, response, headers } = data;
      const containerName = actionInPayload.containerName;
      const target = getResolvedTargetName(containerName, actionInPayload.context);
      let context = getContext(target, containerName, actionInPayload.context); // app/modal_1
      context = buildModalContext(actionInPayload.context, context as string);
      /** context = app/primary_1/workarea_1/modal_1 */
      const { pageReference, targetProperty, index, subscriptFieldName, pageGroupName, actionType, isPageGroup } =
        actionInPayload;
      const isObjectPageReference = pageReference?.startsWith(publicConstants.OBJECT_INFO.OBJECT_INFO);
      let responseJSON: DxAPIResponseObject | null = null;

      if (isObjectPageReference) {
        responseJSON = ObjectTransformer.transformCaseData(response);
        responseJSON.uiResources.root.config.context = `${pageReference}${targetProperty}[${index}]`;
      } else {
        responseJSON = response as DxAPIResponseObject;
      }
      const translatedDXAPIResponse = getTranslatedDXAPIResponse(responseJSON);
      translatedDXAPIResponse.key = actionInPayload.ID;

      if (translatedDXAPIResponse.data?.caseInfo) {
        translatedDXAPIResponse.data.caseInfo.ID = actionInPayload.ID;

        updateCaseInfoContentForPageGroup(
          translatedDXAPIResponse,
          index,
          subscriptFieldName,
          actionType,
          pageGroupName
        );
      } else if (translatedDXAPIResponse.data?.objectInfo) {
        translatedDXAPIResponse.data.objectInfo.ID = actionInPayload.ID;
      } else {
        // do nothing
      }
      const { interestPageActionID, editType, isLocalAction, actionID } = actionInPayload;
      const modalView = {
        isMultiRecordData: true,
        viewName: actionInPayload.viewID,
        /** parentContext = workarea context */
        parentContext: actionInPayload.context,
        interestPage: actionInPayload.interestPage,
        interestPageActionID,
        editType,
        isLocalAction,
        actionID
      };
      translatedDXAPIResponse.uiResources.context_data.modalView = modalView;
      const allErrorMessages = getDataObject(actionInPayload.context)?.context_data?.caseInfo;
      const fullRefPath = `${actionInPayload.pageReference}${actionInPayload.targetProperty}`;
      const rowErrors = getTableRowErrorMessages(allErrorMessages, fullRefPath, actionInPayload.index);

      translatedDXAPIResponse.uiResources.context_data.caseInfo = {
        ...translatedDXAPIResponse.uiResources.context_data.caseInfo,
        content: Utils.mergeDeep(translatedDXAPIResponse.uiResources.context_data.caseInfo?.content, rowErrors?.content)
      };

      // Adding remotesystem ID to modal context
      const remoteSystemID = ContextProcessor.getProperty(
        constants.CASE_INFO.REMOTESYSTEMID,
        '',
        actionInPayload.context
      );

      const options = { acName: target, context };
      await processViewResponse(translatedDXAPIResponse, actionInPayload, options);
      const commonInfo = {
        target,
        context,
        containerItemID: context,
        headers,
        index,
        subscriptFieldName,
        pageGroupName,
        isPageGroup
      };
      if (remoteSystemID) {
        commonInfo.headers = { ...commonInfo.headers, RemoteSystemID: remoteSystemID };
      }
      const content = Utils.deepClone(translatedDXAPIResponse.data?.caseInfo?.content);
      if (translatedDXAPIResponse.data?.caseInfo && editType === constants.ACTION) {
        set(translatedDXAPIResponse.data, `${pageReference}${targetProperty}`, new Array(index + 1).fill({}));
        const initialContent = Utils.deepClone(
          getValue(`${targetProperty}[${index}]`, pageReference, actionInPayload.context) ?? {}
        );

        updateClassIDInInitialContent(initialContent, pageReference, targetProperty, index, content);

        set(translatedDXAPIResponse.data, `${pageReference}${targetProperty}[${index}]`, initialContent);
      }
      const payload = getActionPayload(translatedDXAPIResponse, commonInfo);

      if (translatedDXAPIResponse.data?.caseInfo) {
        translatedDXAPIResponse.data.caseInfo.content = content;
      }
      const mergePayload = getActionPayload(translatedDXAPIResponse, commonInfo);

      payload.resourceStatus = actionInPayload.actionType;
      payload.isMultiRecordData = true;
      payload.parentContext = actionInPayload.context;
      payload.heading = actionInPayload.heading;
      if (payload.data.caseInfo && editType === constants.ACTION) {
        payload.data.caseInfo = {
          ...payload.data.caseInfo,
          ...getActionAndAssignmentID(actionInPayload),
          isLocalAction: actionInPayload.isLocalAction
        };
      }
      payload.clearStateMachine = false;

      mergePayload.skipValidation = true;
      const action = getActionBasedOnContainerMode(target, payload);

      toContext = context;

      actionsOut.push(action);
      addPreviewResetAction(actionsOut);

      const mergeAction = actions.postProcess(actions.mergeData, mergePayload);
      return { actionsOut, actionInPayload, mergeAction };
    }),
    map(({ actionInPayload, mergeAction }) => {
      if (hasErrorActionType(actionsOut)) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
      } else {
        ActionManagerInstance.resolveActionByID(actionMgrID);
      }
      if (!actionInPayload.skipPageInstructions) {
        generateInstructions(
          toContext,
          actionInPayload.pageReference,
          actionInPayload.targetProperty,
          actionInPayload.index,
          actionInPayload.actionType
        );
      }
      dispatch(actionsOut);
      actionsOut = [];
      if (actionInPayload.editType === constants.ACTION) {
        stateMachineManager.registerForServerChanges();
        dispatch(mergeAction);
        stateMachineManager.unRegisterForServerChanges();
      }
      return actionsOut;
    })
  );
};

export default openEmbeddedDataModal;
